import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Background from '../components/Background';
import IEEELogo from '../assets/IEEE NEW LOGO white v2.png';

// ═══════════════════════════════════════════════════════════════
//  BROADCAST MESSAGE
//  Edit this string and push to Vercel — every projector screen
//  at the venue will show the updated message on their next load.
//  Use separate strings in the array for separate bullet lines.
// ═══════════════════════════════════════════════════════════════
const BROADCAST_LINES = [
  'Lunch timings: 12:00 PM – 12:45 PM.',
  'All participants must be back in their labs with everything set up by 12:45 PM.',
  'The hackathon officially starts at 1:00 PM — be ready.',
  'To leave your lab for any reason after the hackathon starts, you must take permission from your assigned volunteer / laborator.',
];

// ───────────────────────────────────────────────────────────────

// Hackathon: March 28, 2026 at 1:00 PM IST, runs for 24 hours
const HACKATHON_START = '2026-03-28T13:00:00';
const HACKATHON_DURATION_HOURS = 24;

function pad(n: number) {
  return String(Math.floor(n)).padStart(2, '0');
}

function calcRemaining(startTime: string, durationHours: number) {
  const elapsed = (Date.now() - new Date(startTime).getTime()) / 1000;
  return Math.max(0, durationHours * 3600 - elapsed);
}

interface TimeDisplay {
  h: string; m: string; s: string; remaining: number;
}

const COLORS = {
  normal: {
    h: { primary: '#00ffff', glow: 'rgba(0,255,255,0.55)',  dim: 'rgba(0,255,255,0.12)',  border: 'rgba(0,255,255,0.28)'  },
    m: { primary: '#818cf8', glow: 'rgba(129,140,248,0.55)', dim: 'rgba(129,140,248,0.12)', border: 'rgba(129,140,248,0.28)' },
    s: { primary: '#f87171', glow: 'rgba(248,113,113,0.55)', dim: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.28)' },
  },
  alert: {
    h: { primary: '#ff0044', glow: 'rgba(255,0,68,0.65)', dim: 'rgba(255,0,68,0.15)', border: 'rgba(255,0,68,0.4)' },
    m: { primary: '#ff0044', glow: 'rgba(255,0,68,0.65)', dim: 'rgba(255,0,68,0.15)', border: 'rgba(255,0,68,0.4)' },
    s: { primary: '#ff0044', glow: 'rgba(255,0,68,0.65)', dim: 'rgba(255,0,68,0.15)', border: 'rgba(255,0,68,0.4)' },
  },
};

export default function Countdown() {
  const [time, setTime]       = useState<TimeDisplay | null>(null);
  const [isAlert, setIsAlert] = useState(false);
  const [isDone, setIsDone]   = useState(false);

  useEffect(() => {
    function tick() {
      const remaining = calcRemaining(HACKATHON_START, HACKATHON_DURATION_HOURS);
      setTime({
        h: pad(Math.floor(remaining / 3600)),
        m: pad(Math.floor((remaining % 3600) / 60)),
        s: pad(Math.floor(remaining % 60)),
        remaining,
      });
      setIsAlert(remaining <= 3600 && remaining > 0);
      setIsDone(remaining === 0);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const palette    = isAlert ? COLORS.alert : COLORS.normal;
  const statusColor = isAlert ? '#ff0044' : '#00ffff';

  return (
    <div className="relative h-screen w-full flex flex-col overflow-hidden bg-black text-white">
      <Background />
      <div className="scanlines" />

      {/* ── Header ─────────────────────────────────────────── */}
      <header className="relative z-20 flex items-center justify-between px-6 sm:px-10 pt-4 pb-2 flex-shrink-0">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }} className="relative">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold leading-none select-none tracking-tight">
            <span style={{ background: 'linear-gradient(135deg,#67e8f9,#22d3ee,#3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Algo</span>
            <span style={{ background: 'linear-gradient(135deg,#f87171,#ef4444,#a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Forge</span>
          </h1>
          <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ delay: 0.5, duration: 0.8 }}
            className="absolute -bottom-0.5 left-0 h-px"
            style={{ background: 'linear-gradient(90deg,#22d3ee,#ef4444)' }}
          />
        </motion.div>
        <motion.img src={IEEELogo} alt="IEEE KJSIT"
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}
          className="h-7 sm:h-9 md:h-11 w-auto object-contain opacity-80"
        />
      </header>

      {/* ── Status line ────────────────────────────────────── */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
        className="relative z-20 flex items-center justify-center gap-3 py-1 flex-shrink-0">
        <motion.span animate={{ opacity: [1, 0.25, 1] }} transition={{ duration: 2, repeat: Infinity }}
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ background: statusColor, boxShadow: `0 0 6px ${statusColor}` }} />
        <span className="font-mono text-[0.55rem] sm:text-[0.65rem] tracking-[0.65em] uppercase"
          style={{ color: isAlert ? '#ff0044' : 'rgba(0,255,255,0.55)' }}>
          {isAlert ? 'Critical — Final Hour' : 'Mission Active · Time Remaining'}
        </span>
        <motion.span animate={{ opacity: [1, 0.25, 1] }} transition={{ duration: 2, repeat: Infinity, delay: 1 }}
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ background: statusColor, boxShadow: `0 0 6px ${statusColor}` }} />
      </motion.div>

      {/* ── Timer panels ───────────────────────────────────── */}
      <main className="relative z-20 flex-1 flex items-center justify-center px-3 sm:px-5 min-h-0">
        <AnimatePresence mode="wait">
          {!time ? (
            <motion.div key="waiting" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="font-mono text-lg tracking-widest text-gray-600">
              Awaiting start signal
              <motion.span animate={{ opacity: [1, 0, 1] }} transition={{ duration: 1, repeat: Infinity }}>_</motion.span>
            </motion.div>
          ) : isDone ? (
            <motion.div key="done" initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <motion.h2 animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 0.6, repeat: Infinity }}
                style={{ fontFamily: "'Orbitron', monospace", fontSize: 'clamp(3rem,10vw,8rem)', fontWeight: 900, color: '#ff0044', textShadow: '0 0 60px rgba(255,0,68,0.7)', letterSpacing: '0.05em' }}>
                TIME'S UP
              </motion.h2>
            </motion.div>
          ) : (
            <motion.div key="timer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
              className="flex items-stretch justify-center w-full gap-2 sm:gap-4 md:gap-5"
              style={{ height: 'clamp(253px, 44vh, 483px)' }}>
              <DigitPanel digits={time.h} label="HOURS"   colors={palette.h} delay={0.1} />
              <DotSeparator color={palette.h.primary} />
              <DigitPanel digits={time.m} label="MINUTES" colors={palette.m} delay={0.2} />
              <DotSeparator color={palette.s.primary} />
              <DigitPanel digits={time.s} label="SECONDS" colors={palette.s} delay={0.3} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* ── Broadcast card ─────────────────────────────────── */}
      {BROADCAST_LINES.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="relative z-20 flex-shrink-0 mx-3 sm:mx-5 mb-3 sm:mb-4"
          style={{
            background: 'linear-gradient(135deg, rgba(0,10,20,0.92), rgba(5,0,15,0.92))',
            border: '1px solid rgba(0,255,255,0.18)',
            boxShadow: '0 0 0 1px rgba(0,255,255,0.06), 0 -4px 30px rgba(0,0,0,0.5)',
          }}
        >
          {/* Top gradient bar */}
          <div className="w-full h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #00ffff 30%, #818cf8 70%, transparent)' }} />

          {/* Scanline */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.012) 3px, rgba(255,255,255,0.012) 4px)' }} />

          {/* Corner brackets */}
          {(['tl','tr','bl','br'] as const).map(pos => (
            <div key={pos} className={[
              'absolute w-4 h-4',
              pos === 'tl' ? 'top-0 left-0 border-t-[2px] border-l-[2px]' : '',
              pos === 'tr' ? 'top-0 right-0 border-t-[2px] border-r-[2px]' : '',
              pos === 'bl' ? 'bottom-0 left-0 border-b-[2px] border-l-[2px]' : '',
              pos === 'br' ? 'bottom-0 right-0 border-b-[2px] border-r-[2px]' : '',
            ].join(' ')} style={{ borderColor: 'rgba(0,255,255,0.5)' }} />
          ))}

          <div className="px-5 sm:px-8 py-3 sm:py-4">
            {/* Broadcast header */}
            <div className="flex items-center gap-3 mb-2 sm:mb-3">
              <motion.span animate={{ opacity: [1, 0.2, 1] }} transition={{ duration: 2.5, repeat: Infinity }}
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: '#00ffff', boxShadow: '0 0 10px #00ffff, 0 0 20px rgba(0,255,255,0.4)' }} />
              <span className="font-mono tracking-[0.45em] uppercase font-semibold"
                style={{ fontSize: 'clamp(0.55rem, 1.1vw, 0.7rem)', color: '#00ffff' }}>
                Broadcast
              </span>
              <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(0,255,255,0.3), transparent)' }} />
            </div>

            {/* Message lines */}
            <ul className="space-y-1 sm:space-y-1.5">
              {BROADCAST_LINES.map((line, i) => (
                <motion.li key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + i * 0.08 }}
                  className="flex items-start gap-2.5"
                >
                  <span className="flex-shrink-0 mt-[0.35em]"
                    style={{ width: 4, height: 4, borderRadius: 2, background: '#00ffff', boxShadow: '0 0 5px #00ffff', display: 'inline-block', opacity: 0.7 }} />
                  <span className="font-mono leading-snug text-gray-200"
                    style={{ fontSize: 'clamp(0.7rem, 1.5vw, 0.95rem)' }}>
                    {line}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </motion.section>
      )}
    </div>
  );
}

// ── Digit panel ────────────────────────────────────────────────
interface PanelColors { primary: string; glow: string; dim: string; border: string; }

function DigitPanel({ digits, label, colors, delay }: { digits: string; label: string; colors: PanelColors; delay: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col relative overflow-hidden h-full"
      style={{
        width: 'clamp(170px, 27vw, 330px)',
        flexShrink: 0,
        background: 'linear-gradient(160deg, rgba(0,0,0,0.93), rgba(4,4,16,0.97))',
        border: `1px solid ${colors.border}`,
        boxShadow: `0 0 0 1px ${colors.dim}, 0 12px 50px rgba(0,0,0,0.7), inset 0 1px 0 ${colors.dim}`,
      }}
    >
      {/* Colour bar */}
      <div className="w-full h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${colors.primary}, transparent)` }} />

      {/* Scanlines */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.016) 3px, rgba(255,255,255,0.016) 4px)' }} />

      {/* Shine sweep */}
      <motion.div className="absolute inset-0 pointer-events-none"
        style={{ background: `linear-gradient(105deg, transparent 25%, ${colors.dim} 50%, transparent 75%)` }}
        animate={{ x: ['-120%', '220%'] }}
        transition={{ duration: 3.5, repeat: Infinity, repeatDelay: 5 + delay * 2, ease: 'easeInOut' }}
      />

      {/* Corner brackets */}
      <div className="absolute top-0 left-0 w-4 h-4 border-t-[2px] border-l-[2px]" style={{ borderColor: colors.primary }} />
      <div className="absolute top-0 right-0 w-4 h-4 border-t-[2px] border-r-[2px]" style={{ borderColor: colors.primary }} />
      <div className="absolute bottom-0 left-0 w-4 h-4 border-b-[2px] border-l-[2px]" style={{ borderColor: colors.primary }} />
      <div className="absolute bottom-0 right-0 w-4 h-4 border-b-[2px] border-r-[2px]" style={{ borderColor: colors.primary }} />

      {/* Content */}
      <div className="flex flex-col items-center justify-center flex-1 gap-2 px-8 py-5">
        <span className="font-mono tracking-[0.45em] uppercase select-none"
          style={{ fontSize: 'clamp(0.5rem, 1.1vw, 0.68rem)', color: colors.primary, opacity: 0.65 }}>
          {label}
        </span>

        {/* Digit with flip */}
        <div className="relative overflow-hidden flex items-center justify-center w-full"
          style={{ height: 'clamp(4.6rem, 14vw, 11.5rem)' }}>
          <AnimatePresence mode="popLayout">
            <motion.span key={digits}
              initial={{ y: '75%', opacity: 0, filter: 'blur(6px)' }}
              animate={{ y: 0,     opacity: 1, filter: 'blur(0px)' }}
              exit={{   y: '-75%', opacity: 0, filter: 'blur(6px)' }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              style={{
                fontFamily:   "'Orbitron', monospace",
                fontSize:     'clamp(4rem, 11.5vw, 10.5rem)',
                fontWeight:   900,
                lineHeight:   1,
                letterSpacing: '-0.04em',
                color:        colors.primary,
                textShadow:   `0 0 25px ${colors.glow}, 0 0 70px ${colors.dim}`,
                display:      'block',
              }}
            >
              {digits}
            </motion.span>
          </AnimatePresence>
        </div>

        {/* Track line */}
        <div className="w-3/4 h-px" style={{ background: `linear-gradient(90deg, transparent, ${colors.border}, transparent)` }} />
      </div>
    </motion.div>
  );
}

// ── Dot separator ───────────────────────────────────────────────
function DotSeparator({ color }: { color: string }) {
  return (
    <div className="flex-shrink-0 flex flex-col items-center justify-center gap-3 pb-8">
      {[0, 1].map(i => (
        <motion.div key={i} className="rounded-full"
          style={{ width: 'clamp(7px, 1.1vw, 12px)', height: 'clamp(7px, 1.1vw, 12px)', background: color, boxShadow: `0 0 10px ${color}, 0 0 24px ${color}55` }}
          animate={{ opacity: [0.55, 1, 0.55], scale: [0.88, 1.08, 0.88] }}
          transition={{ duration: 2.8, repeat: Infinity, delay: i * 0.5, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}
